import React, { useEffect, useState } from "react";
import { Drawer, Form, InputNumber, Button, Icon, message, Input, Tag, Skeleton, Modal, Table, Avatar, Select, Switch, Radio, Popconfirm } from "antd";
import moment from "moment";

import { request } from "~/components/Instance";

var SUPABASE_URL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SUPABASE_URL) || '';
var SUPABASE_ANON_KEY = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SUPABASE_ANON_KEY) || '';

// Actor IDs documented in /Users/redhmam/repositories/bruno/APIFY_GUIDE.md.
// Hardcoded — not user-editable. If an actor breaks we'll publish a new
// frontend build with the replacement IDs.
export var APIFY_ACTORS = {
    profile: 'dSCLg0C3YEZ83HzYX',
    followers: '8dqiL379xy0Ldrhdr',
    likes: 'WxPRaG9gfg5KZ4gY1',
    comments: 'Is2z7LZ6gAOxIehh8',
};

var DEFAULT_LIMITS = {
    maxFollowers: 50,
    maxLikes: 20,
    maxComments: 15,
    maxPostsPerProfile: 6,
};

// Each destination is a CRM domain that consumes harvested contacts.
// `entity_type` matches the parameter the Laravel cadence-summary endpoint
// expects (cadences are stored under different settings keys per domain).
var DESTINATION_META = {
    social_selling: {
        label: 'Social Selling',
        short: 'Social Selling',
        color: '#722ed1',
        bg: '#f9f0ff',
        border: '#d3adf7',
        entity_type: 'socialSelling',
        description: 'Engajamento warm — contatos viram leads para conversa direta no IG.',
    },
    prospeccao_ativa: {
        label: 'Prospecção Ativa',
        short: 'Prospecção Ativa',
        color: '#1890ff',
        bg: '#e6f7ff',
        border: '#91d5ff',
        entity_type: 'prospecting',
        description: 'Outbound frio — lista de prospects para abordagem em massa.',
    },
};

function fetchSupabaseToken() {
    return request('GET', '/supabase-token', null, false).then(function(res) {
        return res && res.data && res.data.token;
    });
}

// Renders an Instagram profile photo through our edge-fn proxy, falling
// back to a gradient avatar with the username initial when (a) we have no
// URL stored, (b) the proxy can't reach upstream, or (c) Instagram's
// signed token has expired (most common — IG URLs die in hours).
var IGAvatar = function(props) {
    var url = props.url;
    var letter = ((props.fallbackChar || '?') + '').charAt(0).toUpperCase();
    var size = props.size || 36;
    var [failed, setFailed] = useState(false);
    var gradient = (
        <Avatar
            size={size}
            style={{
                flexShrink: 0,
                background: 'linear-gradient(135deg, #f56040 0%, #c13584 50%, #833ab4 100%)',
                fontSize: size > 32 ? 14 : 12,
                fontWeight: 600,
                color: '#fff',
            }}
        >
            {letter}
        </Avatar>
    );
    if (!url || failed) return gradient;
    var isBucketUrl = url.indexOf(SUPABASE_URL + '/storage/v1/object/public/apify-avatars/') === 0;
    var src = isBucketUrl ? url : (SUPABASE_URL + '/functions/v1/apify-image-proxy?url=' + encodeURIComponent(url));
    return (
        <Avatar
            src={src}
            size={size}
            style={{ flexShrink: 0, border: '1px solid #f0f0f0' }}
            onError={function() { setFailed(true); return true; }}
        />
    );
};

var SOURCE_META = {
    followers: { label: 'Seguidor', icon: 'user-add', color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff' },
    likes:     { label: 'Curtiu',   icon: 'heart',    color: '#eb2f96', bg: '#fff0f6', border: '#ffadd2' },
    comments:  { label: 'Comentou', icon: 'message',  color: '#722ed1', bg: '#f9f0ff', border: '#d3adf7' },
};

function getErrorCopy(run) {
    var code = run && run.error_code;
    var data = (run && run.error_data) || {};
    if (code === 'actor_approval_required') {
        return {
            headline: 'Aprovação necessária no Apify',
            cta: data.approval_url ? { label: 'Aprovar no Apify', url: data.approval_url, primary: true } : null,
            hint: 'Clique no botão, autorize o acesso na sua conta Apify e tente buscar de novo.',
        };
    }
    if (code === 'invalid_token') {
        return {
            headline: 'Chave de acesso inválida',
            cta: { label: 'Abrir Apify', url: 'https://console.apify.com/settings/integrations', primary: false },
            hint: 'Gere uma nova chave em Settings → API & Integrations e cole no campo acima.',
        };
    }
    if (code === 'out_of_credit') {
        return {
            headline: 'Sem créditos no Apify',
            cta: { label: 'Adicionar créditos', url: 'https://console.apify.com/billing', primary: true },
            hint: null,
        };
    }
    if (code === 'rate_limited') {
        return { headline: 'Limite atingido temporariamente', cta: null, hint: 'Aguarde alguns minutos e tente de novo.' };
    }
    if (code === 'apify_unavailable') {
        return { headline: 'Apify temporariamente indisponível', cta: null, hint: 'Tente de novo em alguns minutos.' };
    }
    if (code === 'timeout') {
        return { headline: 'Busca demorou demais', cta: null, hint: 'Diminua "Seguidores por perfil" nas configurações e tente de novo.' };
    }
    if (code === 'network_error') {
        return { headline: 'Falha de conexão com o Apify', cta: null, hint: null };
    }
    if (code === 'db_upsert_failed') {
        return { headline: 'Erro ao salvar contatos', cta: null, hint: 'A busca rodou no Apify mas a gravação falhou. Tente de novo.' };
    }
    if (code === 'actor_unavailable') {
        return { headline: 'Scraper indisponível', cta: null, hint: 'O recurso do Apify pode ter sido removido. Avise o suporte.' };
    }
    return { headline: 'Falha na busca', cta: null, hint: null };
}

function normalizeUsernameClient(raw) {
    if (!raw) return '';
    var s = String(raw).trim();
    s = s.replace(/^https?:\/\//i, '');
    s = s.replace(/^(www\.)?instagram\.com\//i, '');
    s = s.split('?')[0].split('#')[0];
    s = s.replace(/\/+$/, '');
    s = s.replace(/^@/, '');
    s = s.split('/')[0];
    return s.trim();
}

function pushUniqueUsername(list, raw) {
    var u = normalizeUsernameClient(raw);
    if (!u) return list;
    var key = u.toLowerCase();
    for (var i = 0; i < list.length; i++) {
        if (list[i].toLowerCase() === key) return list;
    }
    return list.concat([u]);
}

function limitsToCamel(api) {
    if (!api) return Object.assign({}, DEFAULT_LIMITS);
    return {
        maxFollowers: api.max_followers != null ? api.max_followers : DEFAULT_LIMITS.maxFollowers,
        maxLikes: api.max_likes != null ? api.max_likes : DEFAULT_LIMITS.maxLikes,
        maxComments: api.max_comments != null ? api.max_comments : DEFAULT_LIMITS.maxComments,
        maxPostsPerProfile: api.max_posts_per_profile != null ? api.max_posts_per_profile : DEFAULT_LIMITS.maxPostsPerProfile,
    };
}
function limitsToSnake(camel) {
    return {
        max_followers: camel.maxFollowers,
        max_likes: camel.maxLikes,
        max_comments: camel.maxComments,
        max_posts_per_profile: camel.maxPostsPerProfile,
    };
}

// AntD v3's Skeleton has no Input subcomponent — small shimmer div.
var SkeletonInputPlaceholder = function(props) {
    return (
        <div
            style={Object.assign({
                width: '100%',
                height: props.height || 32,
                borderRadius: 4,
                background: 'linear-gradient(90deg, #f2f2f2 25%, #e8e8e8 37%, #f2f2f2 63%)',
                backgroundSize: '400% 100%',
                animation: 'apify-shimmer 1.4s ease infinite',
            }, props.style || {})}
        />
    );
};

var ContactsTableSkeleton = function(props) {
    var rowCount = props.rows || 6;
    var skeletonRows = [];
    for (var i = 0; i < rowCount; i++) {
        skeletonRows.push(
            <div
                key={i}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderBottom: '1px solid #fafafa',
                    gap: 16,
                }}
            >
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <SkeletonInputPlaceholder style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <SkeletonInputPlaceholder style={{ width: '38%', height: 13, marginBottom: 6 }} />
                        <SkeletonInputPlaceholder style={{ width: '64%', height: 11 }} />
                    </div>
                </div>
                <div style={{ width: 110, flexShrink: 0 }}>
                    <SkeletonInputPlaceholder style={{ width: 78, height: 20, borderRadius: 12 }} />
                </div>
                <div style={{ width: 130, flexShrink: 0 }}>
                    <SkeletonInputPlaceholder style={{ width: '72%', height: 12 }} />
                </div>
                <div style={{ width: 90, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                    <SkeletonInputPlaceholder style={{ width: 56, height: 12 }} />
                </div>
            </div>,
        );
    }
    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    background: '#fafafa',
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#595959',
                    gap: 16,
                }}
            >
                <div style={{ flex: 1 }}>Perfil</div>
                <div style={{ width: 110 }}>Origem</div>
                <div style={{ width: 130 }}>Adicionado</div>
                <div style={{ width: 90 }} />
            </div>
            {skeletonRows}
            <style>{
                '@keyframes apify-shimmer {' +
                '  0%   { background-position: 100% 50%; }' +
                '  100% { background-position: 0%   50%; }' +
                '}'
            }</style>
        </div>
    );
};

var IntegrationsListSkeleton = function() {
    return (
        <div>
            {[0, 1].map(function(i) {
                return (
                    <div key={i} style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <SkeletonInputPlaceholder style={{ width: 36, height: 36, borderRadius: 8 }} />
                            <div style={{ flex: 1 }}>
                                <SkeletonInputPlaceholder style={{ width: '40%', height: 13, marginBottom: 6 }} />
                                <SkeletonInputPlaceholder style={{ width: '70%', height: 11 }} />
                            </div>
                            <SkeletonInputPlaceholder style={{ width: 80, height: 24, borderRadius: 4 }} />
                        </div>
                    </div>
                );
            })}
            <style>{
                '@keyframes apify-shimmer {' +
                '  0%   { background-position: 100% 50%; }' +
                '  100% { background-position: 0%   50%; }' +
                '}'
            }</style>
        </div>
    );
};

// Cabeçalho numerado das seções do formulário — guia o usuário na ordem
// natural de preenchimento (nome → chave → perfis → destino → automação).
var StepHeader = function(props) {
    return (
        <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: '#fff7e6', border: '1px solid #ffd591', color: '#ad4e00',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700, flexShrink: 0,
                }}>
                    {props.number}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#262626' }}>{props.title}</div>
                {props.extra || null}
            </div>
            {props.hint && (
                <div style={{ fontSize: 11, color: '#8c8c8c', margin: '4px 0 0 28px', lineHeight: 1.5 }}>{props.hint}</div>
            )}
        </div>
    );
};

// Passo a passo colapsável de como obter a chave do Apify — para quem nunca
// usou o Apify; quem já tem a chave simplesmente ignora.
var TokenHowTo = function() {
    var [open, setOpen] = useState(false);
    var steps = [
        <span>Crie uma conta gratuita em <a href="https://console.apify.com/sign-up" target="_blank" rel="noopener noreferrer">apify.com</a> — leva uns 2 minutos.</span>,
        <span>No menu da sua conta, abra <strong>Settings → API &amp; Integrations</strong>.</span>,
        <span>Copie o <strong>Personal API token</strong> (começa com <span style={{ fontFamily: 'monospace' }}>apify_api_</span>) e cole no campo acima.</span>,
    ];
    return (
        <div style={{ marginTop: 6 }}>
            <a onClick={function() { setOpen(!open); }} style={{ fontSize: 11 }}>
                <Icon type="question-circle" style={{ marginRight: 4 }} />
                Não tem uma chave? Veja o passo a passo
                <Icon type={open ? 'up' : 'down'} style={{ fontSize: 9, marginLeft: 4 }} />
            </a>
            {open && (
                <div style={{ marginTop: 8, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 6, padding: '10px 12px' }}>
                    {steps.map(function(step, i) {
                        return (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                                <div style={{
                                    width: 16, height: 16, borderRadius: '50%', background: '#fafafa',
                                    border: '1px solid #e8e8e8', color: '#595959',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 10, fontWeight: 600, flexShrink: 0, marginTop: 1,
                                }}>
                                    {i + 1}
                                </div>
                                <div style={{ fontSize: 11, color: '#595959', lineHeight: 1.5, flex: 1 }}>{step}</div>
                            </div>
                        );
                    })}
                    <Button
                        size="small"
                        icon="export"
                        href="https://console.apify.com/settings/integrations"
                        target="_blank"
                        style={{ fontSize: 11 }}
                    >
                        Abrir página da chave no Apify
                    </Button>
                </div>
            )}
        </div>
    );
};

// Pulls cadences for a given CRM domain. Backend endpoint:
// /leads_v2/cadence-summary?entity_type=socialSelling | prospecting
// Mints a ServiceKey on the Laravel side so the Apify worker can call
// upsert-from-instagram with X-Services-Key auth. Returns the key string
// or throws. Called when the user turns "Criar lead automaticamente" on.
function mintLaravelServiceKey() {
    return request('POST', '/auth/apify/mint-service-key', {}, false).then(function(res) {
        var key = res && res.data && res.data.key;
        if (!key) throw new Error('Não foi possível gerar a chave de conexão.');
        return key;
    });
}

function loadCadenceList(entityType) {
    return request('GET', '/leads_v2/cadence-summary?entity_type=' + entityType, null, false)
        .then(function(res) {
            // The endpoint returns a summary; the cadence rows surface their
            // names via various shapes. We accept either an array of strings,
            // an array of objects with `name`, or a wrapped { data } payload.
            var raw = res && res.data;
            var rows = [];
            if (raw && Array.isArray(raw)) rows = raw;
            else if (raw && Array.isArray(raw.data)) rows = raw.data;
            else if (raw && Array.isArray(raw.cadences)) rows = raw.cadences;
            else if (raw && raw.payload && Array.isArray(raw.payload.data)) rows = raw.payload.data;

            var names = [];
            var seen = {};
            for (var i = 0; i < rows.length; i++) {
                var row = rows[i];
                var name = typeof row === 'string' ? row : (row && (row.name || row.cadence || row.cadence_name));
                if (!name) continue;
                if (seen[name]) continue;
                seen[name] = true;
                names.push(name);
            }
            return names;
        })
        .catch(function() { return []; });
}

// ─── Main component ────────────────────────────────────────────────────

var ApifyIntegrationDrawer = function(props) {
    var visible = props.visible;
    var onClose = props.onClose;

    // List + collection state
    var [integrations, setIntegrations] = useState([]);
    var [loadingList, setLoadingList] = useState(false);
    var [expandedId, setExpandedId] = useState(null); // integration.id, or '__new__' for the create form, or null

    // Form state for the currently-edited / created integration
    var [editForm, setEditForm] = useState(null);
    var [usernameInput, setUsernameInput] = useState('');
    var [saving, setSaving] = useState(false);
    var [scrapingId, setScrapingId] = useState(null);

    // Cadence options keyed by destination
    var [cadenceOptions, setCadenceOptions] = useState({ social_selling: [], prospeccao_ativa: [] });

    // Contacts modal (drilldown into a specific run)
    var [contactsModalRun, setContactsModalRun] = useState(null);
    var [modalContacts, setModalContacts] = useState([]);
    var [modalContactsLoading, setModalContactsLoading] = useState(false);
    var [modalContactsTotal, setModalContactsTotal] = useState(0);

    // ── Loaders ────────────────────────────────────────────────────────
    function loadIntegrations() {
        setLoadingList(true);
        return fetchSupabaseToken()
            .then(function(jwt) {
                if (!jwt) throw new Error('no-token');
                return fetch(SUPABASE_URL + '/functions/v1/apify-integrations', {
                    method: 'GET',
                    headers: { 'Authorization': 'Bearer ' + jwt, 'apikey': SUPABASE_ANON_KEY },
                });
            })
            .then(function(res) { return res.ok ? res.json() : { integrations: [] }; })
            .then(function(data) {
                setIntegrations(Array.isArray(data.integrations) ? data.integrations : []);
            })
            .catch(function() { setIntegrations([]); })
            .finally(function() { setLoadingList(false); });
    }

    function loadCadences() {
        Promise.all([loadCadenceList('socialSelling'), loadCadenceList('prospecting')])
            .then(function(parts) {
                setCadenceOptions({ social_selling: parts[0], prospeccao_ativa: parts[1] });
            });
    }

    useEffect(function() {
        if (!visible) return;
        setExpandedId(null);
        setEditForm(null);
        loadIntegrations();
        loadCadences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    // ── Edit form lifecycle ────────────────────────────────────────────
    function startNew() {
        setExpandedId('__new__');
        setEditForm({
            id: null,
            name: '',
            token: '',
            target_usernames: [],
            destination: null,
            cadence_name: null,
            limits: Object.assign({}, DEFAULT_LIMITS),
            auto_promote: false,
        });
        setUsernameInput('');
    }

    function startEdit(integration) {
        setExpandedId(integration.id);
        setEditForm({
            id: integration.id,
            name: integration.name || '',
            token: '', // never pre-fill an existing token; user can leave blank to keep
            has_token: !!integration.has_token,
            token_preview: integration.token_preview || null,
            target_usernames: Array.isArray(integration.target_usernames) ? integration.target_usernames.slice() : [],
            destination: integration.destination,
            cadence_name: integration.cadence_name || null,
            limits: limitsToCamel(integration.limits),
            auto_promote: !!integration.auto_promote,
            has_laravel_service_key: !!integration.has_laravel_service_key,
        });
        setUsernameInput('');
    }

    function cancelEdit() {
        setExpandedId(null);
        setEditForm(null);
        setUsernameInput('');
    }

    function updateForm(patch) {
        setEditForm(function(prev) { return Object.assign({}, prev || {}, patch); });
    }

    function updateLimits(key, value) {
        setEditForm(function(prev) {
            if (!prev) return prev;
            var next = Object.assign({}, prev.limits);
            next[key] = value;
            return Object.assign({}, prev, { limits: next });
        });
    }

    // Flush the pending tag input into the array, then call cb with the new
    // array of usernames. Used both on save and on chip-add events.
    function withFlushedUsername(cb) {
        var next = pushUniqueUsername(editForm.target_usernames, usernameInput);
        if (next !== editForm.target_usernames) {
            setEditForm(function(prev) { return Object.assign({}, prev, { target_usernames: next }); });
            setUsernameInput('');
        }
        if (cb) cb(next);
    }

    // ── Save / delete / scrape ─────────────────────────────────────────
    function saveIntegration() {
        if (!editForm) return;
        var isNew = editForm.id == null;

        // Validation
        if (!editForm.name || !editForm.name.trim()) {
            message.error('Informe um nome para a integração.');
            return;
        }
        if (!editForm.destination) {
            message.error('Escolha o destino dos contatos (Social Selling ou Prospecção Ativa).');
            return;
        }
        if (isNew && (!editForm.token || !editForm.token.trim())) {
            message.error('Informe a chave de acesso do Apify.');
            return;
        }

        // Flush pending typed username
        var finalTargets = pushUniqueUsername(editForm.target_usernames, usernameInput);
        if (finalTargets !== editForm.target_usernames) {
            setEditForm(function(prev) { return Object.assign({}, prev, { target_usernames: finalTargets }); });
            setUsernameInput('');
        }

        setSaving(true);
        var payload = {
            name: editForm.name.trim(),
            destination: editForm.destination,
            target_usernames: finalTargets,
            cadence_name: editForm.cadence_name || null,
            auto_promote: !!editForm.auto_promote,
            limits: limitsToSnake(editForm.limits),
        };
        var typedToken = (editForm.token || '').trim();
        if (typedToken) payload.token = typedToken;

        var url = SUPABASE_URL + '/functions/v1/apify-integrations' + (isNew ? '' : '/' + editForm.id);
        var method = isNew ? 'POST' : 'PUT';

        // If auto-promote is being turned on for the first time (no existing
        // Laravel key yet), mint one before saving so the worker has what
        // it needs on the very next scrape.
        var needsKey = !!editForm.auto_promote && !editForm.has_laravel_service_key;
        var prep = needsKey
            ? mintLaravelServiceKey().then(function(k) { payload.laravel_service_key = k; })
            : Promise.resolve();

        prep
            .then(function() { return fetchSupabaseToken(); })
            .then(function(jwt) {
                return fetch(url, {
                    method: method,
                    headers: {
                        'Authorization': 'Bearer ' + jwt,
                        'apikey': SUPABASE_ANON_KEY,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });
            })
            .then(function(res) { return res.json().then(function(b) { return { ok: res.ok, body: b }; }); })
            .then(function(result) {
                if (!result.ok) throw new Error((result.body && result.body.error) || 'Falha ao salvar');
                message.success(isNew ? 'Integração criada.' : 'Integração atualizada.');
                return loadIntegrations().then(function() {
                    if (isNew && result.body && result.body.integration) {
                        // Auto-expand the new card so the user sees the runs log surface.
                        setExpandedId(result.body.integration.id);
                        startEdit(result.body.integration);
                    } else {
                        setExpandedId(null);
                        setEditForm(null);
                    }
                });
            })
            .catch(function(err) { message.error(err.message || 'Erro ao salvar'); })
            .finally(function() { setSaving(false); });
    }

    function deleteIntegration(id) {
        fetchSupabaseToken()
            .then(function(jwt) {
                return fetch(SUPABASE_URL + '/functions/v1/apify-integrations/' + id, {
                    method: 'DELETE',
                    headers: { 'Authorization': 'Bearer ' + jwt, 'apikey': SUPABASE_ANON_KEY },
                });
            })
            .then(function(res) {
                if (!res.ok) throw new Error('delete-failed');
                message.success('Integração removida.');
                if (expandedId === id) cancelEdit();
                return loadIntegrations();
            })
            .catch(function() { message.error('Erro ao remover integração.'); });
    }

    function runScrape(integration) {
        setScrapingId(integration.id);
        fetchSupabaseToken()
            .then(function(jwt) {
                return fetch(SUPABASE_URL + '/functions/v1/apify-scrape', {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + jwt,
                        'apikey': SUPABASE_ANON_KEY,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ integration_id: integration.id }),
                });
            })
            .then(function(res) { return res.json().then(function(b) { return { ok: res.ok, body: b }; }); })
            .then(function(result) {
                if (!result.ok) throw new Error((result.body && result.body.error) || 'Falha ao buscar');
                var runs = (result.body && result.body.runs) || [];
                var totalNew = 0;
                var firstFailedCode = null;
                for (var i = 0; i < runs.length; i++) {
                    totalNew += runs[i].new_contacts_count || 0;
                    if (runs[i].status === 'failed' && !firstFailedCode) firstFailedCode = runs[i].error_code || 'unknown';
                }
                if (runs.length === 0) {
                    message.warning('Nenhum perfil para buscar.');
                } else if (firstFailedCode === 'actor_approval_required') {
                    message.warning('O Apify pediu uma aprovação. Veja o botão "Aprovar no Apify" no histórico abaixo.', 6);
                } else if (firstFailedCode === 'invalid_token') {
                    message.warning('A chave do Apify está inválida. Atualize-a acima e tente de novo.', 6);
                } else if (firstFailedCode === 'out_of_credit') {
                    message.warning('Sua conta Apify ficou sem créditos. Veja o histórico abaixo.', 6);
                } else if (firstFailedCode) {
                    message.warning('Busca concluída com falhas. Veja o histórico abaixo.');
                } else {
                    message.success('Busca concluída — ' + totalNew + ' novo(s) contato(s).');
                }
                return loadIntegrations();
            })
            .catch(function(err) { message.error(err.message || 'Erro ao buscar contatos.'); })
            .finally(function() { setScrapingId(null); });
    }

    // ── Contacts modal ─────────────────────────────────────────────────
    function openContactsModal(run) {
        setContactsModalRun(run);
        setModalContacts([]);
        setModalContactsTotal(0);
        setModalContactsLoading(true);
        fetchSupabaseToken()
            .then(function(jwt) {
                var qs = '?source=' + encodeURIComponent(run.target_username);
                return fetch(SUPABASE_URL + '/functions/v1/apify-contacts' + qs, {
                    method: 'GET',
                    headers: { 'Authorization': 'Bearer ' + jwt, 'apikey': SUPABASE_ANON_KEY },
                });
            })
            .then(function(res) { return res.ok ? res.json() : null; })
            .then(function(data) {
                if (!data) return;
                setModalContacts(Array.isArray(data.contacts) ? data.contacts : []);
                setModalContactsTotal(typeof data.total === 'number' ? data.total : 0);
            })
            .catch(function() { message.error('Erro ao carregar contatos'); })
            .finally(function() { setModalContactsLoading(false); });
    }
    function closeContactsModal() {
        setContactsModalRun(null);
        setModalContacts([]);
        setModalContactsTotal(0);
    }

    var contactsColumns = [
        {
            title: 'Perfil',
            dataIndex: 'ig_username',
            key: 'profile',
            render: function(u, row) {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <IGAvatar url={row.profile_pic_url} fallbackChar={u} size={36} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <a
                                    href={'https://instagram.com/' + u}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 500, color: '#262626' }}
                                >
                                    @{u}
                                </a>
                                {row.is_verified && (
                                    <Icon type="check-circle" theme="filled" style={{ color: '#1890ff', fontSize: 11 }} title="Verificado" />
                                )}
                                {row.is_private && (
                                    <Icon type="lock" style={{ color: '#bfbfbf', fontSize: 11 }} title="Perfil privado" />
                                )}
                            </div>
                            {row.full_name && (
                                <div style={{ fontSize: 12, color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.full_name}
                                </div>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Origem',
            dataIndex: 'source_actor',
            key: 'source_actor',
            width: 110,
            render: function(actor, row) {
                var meta = SOURCE_META[actor] || { label: actor || '—', icon: 'question', color: '#8c8c8c', bg: '#fafafa', border: '#f0f0f0' };
                var hint = null;
                if (actor === 'comments' && row.source_comment_text) {
                    hint = '"' + String(row.source_comment_text).slice(0, 140) + (row.source_comment_text.length > 140 ? '…' : '') + '"';
                } else if (actor === 'likes' && row.source_post_url) {
                    hint = 'Curtiu: ' + row.source_post_url;
                }
                return (
                    <span title={hint || undefined} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
                        borderRadius: 12, background: meta.bg, border: '1px solid ' + meta.border,
                        color: meta.color, fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
                    }}>
                        <Icon type={meta.icon} style={{ fontSize: 10 }} />
                        {meta.label}
                    </span>
                );
            },
        },
        {
            title: 'Adicionado',
            dataIndex: 'first_seen_at',
            key: 'first_seen_at',
            width: 130,
            render: function(t) { return <span style={{ fontSize: 12, color: '#8c8c8c' }}>{moment(t).fromNow()}</span>; },
        },
        {
            title: '',
            key: 'action',
            width: 90,
            align: 'right',
            render: function(_, row) {
                return (
                    <a href={'https://instagram.com/' + row.ig_username} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                        Abrir <Icon type="export" style={{ fontSize: 10 }} />
                    </a>
                );
            },
        },
    ];

    // ── Render helpers ─────────────────────────────────────────────────
    function renderDestinationChip(dest, opts) {
        var meta = DESTINATION_META[dest];
        if (!meta) return null;
        var size = (opts && opts.size) || 'sm';
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: size === 'sm' ? '1px 8px' : '3px 10px',
                borderRadius: 12,
                background: meta.bg, border: '1px solid ' + meta.border, color: meta.color,
                fontSize: size === 'sm' ? 10 : 11, fontWeight: 500, whiteSpace: 'nowrap',
            }}>
                {meta.short}
            </span>
        );
    }

    function renderRunsLog(runs, integrationContactsCount) {
        if (!runs || runs.length === 0) {
            return (
                <div style={{ fontSize: 12, color: '#8c8c8c', textAlign: 'center', padding: '14px 0' }}>
                    Nenhuma busca executada ainda.
                </div>
            );
        }
        return (
            <div>
                {runs.slice(0, 6).map(function(run) {
                    var isSuccess = run.status === 'success';
                    var isFailed = run.status === 'failed';
                    var isRunning = run.status === 'running';
                    var statusColor = isSuccess ? '#52c41a' : isFailed ? '#f5222d' : '#FF7A00';
                    var statusBg = isSuccess ? '#f6ffed' : isFailed ? '#fff1f0' : '#fff7e6';
                    var statusIcon = isSuccess ? 'check-circle' : isFailed ? 'close-circle' : 'sync';
                    var errorCopy = isFailed ? getErrorCopy(run) : null;
                    var isClickable = isSuccess && (run.items_count || 0) > 0;
                    return (
                        <div
                            key={run.id}
                            onClick={isClickable ? function() { openContactsModal(run); } : undefined}
                            style={{
                                display: 'flex', alignItems: 'flex-start', gap: 10,
                                padding: '8px 0', borderBottom: '1px solid #fafafa', fontSize: 12,
                                cursor: isClickable ? 'pointer' : 'default',
                            }}
                            onMouseEnter={isClickable ? function(e) { e.currentTarget.style.background = '#fafafa'; } : undefined}
                            onMouseLeave={isClickable ? function(e) { e.currentTarget.style.background = 'transparent'; } : undefined}
                            title={isClickable ? 'Ver contatos encontrados' : undefined}
                        >
                            <div style={{
                                width: 22, height: 22, borderRadius: '50%', background: statusBg,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1,
                            }}>
                                <Icon type={statusIcon} spin={isRunning} style={{ color: statusColor, fontSize: 11 }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                                    <span style={{ fontFamily: 'monospace', color: '#262626', fontWeight: 500 }}>@{run.target_username}</span>
                                    {run.triggered_by === 'manual' && <span style={{ fontSize: 10, color: '#bfbfbf' }}>· manual</span>}
                                </div>
                                {isFailed ? (
                                    <div style={{ marginTop: 6, background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 6, padding: '8px 10px' }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#a8071a', marginBottom: 2 }}>{errorCopy.headline}</div>
                                        <div style={{ fontSize: 11, color: '#595959', lineHeight: 1.5 }}>
                                            {run.error_message || 'Falha desconhecida.'}
                                        </div>
                                        {errorCopy.hint && (
                                            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>{errorCopy.hint}</div>
                                        )}
                                        {errorCopy.cta && (
                                            <div style={{ marginTop: 8 }}>
                                                <Button
                                                    type={errorCopy.cta.primary ? 'primary' : 'default'}
                                                    size="small"
                                                    icon="link"
                                                    href={errorCopy.cta.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={errorCopy.cta.primary ? { background: '#cf1322', borderColor: '#cf1322' } : undefined}
                                                >
                                                    {errorCopy.cta.label}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 1 }}>
                                        {isRunning ? 'Em andamento…' : (
                                            <span>
                                                {run.items_count || 0} {run.items_count === 1 ? 'contato' : 'contatos'}
                                                {run.new_contacts_count > 0 && (
                                                    <span style={{ color: '#52c41a', fontWeight: 500 }}> · {run.new_contacts_count} novo(s)</span>
                                                )}
                                                {isClickable && <span style={{ color: '#FF7A00', marginLeft: 6 }}>· ver <Icon type="right" style={{ fontSize: 9 }} /></span>}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div style={{ fontSize: 10, color: '#bfbfbf', flexShrink: 0, textAlign: 'right' }}>
                                {moment(run.started_at).fromNow()}
                                {run.duration_ms ? <div style={{ fontSize: 9 }}>{Math.round(run.duration_ms / 1000)}s</div> : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    function renderEditForm() {
        if (!editForm) return null;
        var destMeta = editForm.destination ? DESTINATION_META[editForm.destination] : null;
        var availableCadences = editForm.destination ? (cadenceOptions[editForm.destination] || []) : [];

        return (
            <div style={{ background: '#fafafa', borderTop: '1px solid #f0f0f0', padding: 16 }}>
                {/* Passo 1 — Name */}
                <div style={{ marginBottom: 16 }}>
                    <StepHeader
                        number={1}
                        title="Dê um nome para a integração"
                        hint="Só para você identificar depois. Ex: 'Seguidores do concorrente X'."
                    />
                    <Input
                        placeholder="ex: Meus seguidores, Concorrente X…"
                        value={editForm.name}
                        onChange={function(e) { updateForm({ name: e.target.value }); }}
                        disabled={saving}
                    />
                </div>

                {/* Passo 2 — Token */}
                <div style={{ marginBottom: 16 }}>
                    <StepHeader
                        number={2}
                        title="Conecte sua conta Apify"
                        hint="O Apify é o robô que faz as buscas no Instagram. Cole aqui a chave da sua conta."
                        extra={<Icon type="key" style={{ color: '#FF7A00', fontSize: 12 }} />}
                    />
                    {editForm.has_token && (
                        <div style={{
                            background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6,
                            padding: '6px 10px', marginBottom: 8, fontSize: 11,
                            display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                            <Icon type="lock" style={{ color: '#52c41a' }} />
                            <span style={{ color: '#262626', fontWeight: 500 }}>Configurada</span>
                            <span style={{ fontFamily: 'monospace', color: '#8c8c8c' }}>{editForm.token_preview}</span>
                        </div>
                    )}
                    <Input.Password
                        placeholder={editForm.has_token ? 'Cole nova chave para substituir (opcional)' : 'Cole a chave do Apify (apify_api_…)'}
                        value={editForm.token}
                        onChange={function(e) { updateForm({ token: e.target.value }); }}
                        disabled={saving}
                        style={{ fontFamily: 'monospace' }}
                    />
                    <TokenHowTo />
                </div>

                {/* Passo 3 — Targets */}
                <div style={{ marginBottom: 16 }}>
                    <StepHeader
                        number={3}
                        title="Escolha os perfis para rastrear"
                        hint="Os contatos serão capturados dos seguidores destes perfis. Dica: concorrentes, parceiros e perfis do seu nicho rendem os melhores contatos."
                        extra={editForm.target_usernames.length > 0 ? (
                            <span style={{ fontWeight: 400, fontSize: 11, color: '#8c8c8c' }}>
                                {editForm.target_usernames.length} {editForm.target_usernames.length === 1 ? 'perfil' : 'perfis'}
                            </span>
                        ) : null}
                    />
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 6, lineHeight: 1.5 }}>
                        Cole o @ ou o link do perfil e pressione <kbd style={{ padding: '0 4px', background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 3, fontSize: 10 }}>Enter</kbd> (ou vírgula) entre perfis.
                    </div>
                    {editForm.target_usernames.length > 0 && (
                        <div style={{ marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {editForm.target_usernames.map(function(u) {
                                return (
                                    <Tag
                                        key={u}
                                        closable={!saving}
                                        onClose={function(e) {
                                            if (e && e.preventDefault) e.preventDefault();
                                            updateForm({ target_usernames: editForm.target_usernames.filter(function(x) { return x !== u; }) });
                                        }}
                                        style={{ fontFamily: 'monospace', fontSize: 12, background: '#fff7e6', borderColor: '#ffd591', color: '#ad4e00' }}
                                    >
                                        @{u}
                                    </Tag>
                                );
                            })}
                        </div>
                    )}
                    <Input
                        placeholder={editForm.target_usernames.length === 0 ? 'ex: ocontadordomedico' : 'Adicionar outro perfil…'}
                        addonBefore="@"
                        value={usernameInput}
                        onChange={function(e) {
                            var v = e.target.value;
                            if (v.indexOf(',') !== -1) {
                                var parts = v.split(',');
                                var lastPart = parts.pop();
                                var next = editForm.target_usernames;
                                for (var i = 0; i < parts.length; i++) next = pushUniqueUsername(next, parts[i]);
                                if (next !== editForm.target_usernames) updateForm({ target_usernames: next });
                                setUsernameInput(lastPart);
                                return;
                            }
                            setUsernameInput(v);
                        }}
                        onPressEnter={function(e) {
                            if (e && e.preventDefault) e.preventDefault();
                            var next = pushUniqueUsername(editForm.target_usernames, usernameInput);
                            if (next !== editForm.target_usernames) {
                                updateForm({ target_usernames: next });
                                setUsernameInput('');
                            } else if (usernameInput) setUsernameInput('');
                        }}
                        onBlur={function() {
                            if (!usernameInput) return;
                            var next = pushUniqueUsername(editForm.target_usernames, usernameInput);
                            if (next !== editForm.target_usernames) {
                                updateForm({ target_usernames: next });
                                setUsernameInput('');
                            }
                        }}
                        disabled={saving}
                        style={{ fontFamily: 'monospace' }}
                    />
                </div>

                {/* Passo 4 — Destination + cadence */}
                <div style={{ marginBottom: 16 }}>
                    <StepHeader
                        number={4}
                        title="Para onde vão os contatos?"
                        hint="Escolha em qual área do CRM os contatos capturados vão aparecer."
                    />
                    <Radio.Group
                        value={editForm.destination}
                        onChange={function(e) { updateForm({ destination: e.target.value, cadence_name: null }); }}
                        disabled={saving}
                    >
                        <Radio value="social_selling">{DESTINATION_META.social_selling.label}</Radio>
                        <Radio value="prospeccao_ativa">{DESTINATION_META.prospeccao_ativa.label}</Radio>
                    </Radio.Group>
                    {destMeta && (
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4, lineHeight: 1.5 }}>
                            {destMeta.description}
                        </div>
                    )}
                    <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#595959', marginBottom: 4 }}>
                            Cadência
                            <span style={{ fontWeight: 400, color: '#8c8c8c', marginLeft: 6 }}>
                                · opcional{editForm.destination ? ', disponíveis em ' + destMeta.short : ''}
                            </span>
                        </div>
                        {!editForm.destination ? (
                            <div style={{ fontSize: 11, color: '#bfbfbf', padding: '6px 0' }}>
                                Escolha o destino acima para ver as cadências disponíveis.
                            </div>
                        ) : availableCadences.length === 0 ? (
                            <div style={{ fontSize: 11, color: '#8c8c8c', padding: '6px 0' }}>
                                Nenhuma cadência cadastrada em {destMeta.short}. Crie uma em Configurações para usá-la aqui.
                            </div>
                        ) : (
                            <Select
                                value={editForm.cadence_name || undefined}
                                onChange={function(v) { updateForm({ cadence_name: v || null }); }}
                                placeholder="Sem cadência"
                                allowClear
                                style={{ width: '100%' }}
                                disabled={saving}
                            >
                                {availableCadences.map(function(name) {
                                    return <Select.Option key={name} value={name}>{name}</Select.Option>;
                                })}
                            </Select>
                        )}
                    </div>
                </div>

                {/* Ajustes finos — limits */}
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#262626', marginBottom: 2 }}>
                        <Icon type="sliders" style={{ marginRight: 4, color: '#8c8c8c' }} />
                        Ajustes finos
                    </div>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 6, lineHeight: 1.5 }}>
                        Quantos contatos buscar de cada vez. Valores menores gastam menos créditos do Apify por busca.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div>
                            <div style={{ fontSize: 11, color: '#595959', marginBottom: 2 }}>Seguidores por perfil</div>
                            <InputNumber
                                min={1} max={5000}
                                value={editForm.limits.maxFollowers}
                                onChange={function(v) { updateLimits('maxFollowers', v); }}
                                disabled={saving}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div>
                            <div style={{ fontSize: 11, color: '#595959', marginBottom: 2 }}>Curtidas por post <span style={{ color: '#bfbfbf' }}>(em breve)</span></div>
                            <InputNumber
                                min={1} max={1000}
                                value={editForm.limits.maxLikes}
                                onChange={function(v) { updateLimits('maxLikes', v); }}
                                disabled={saving}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div>
                            <div style={{ fontSize: 11, color: '#595959', marginBottom: 2 }}>Comentários por post <span style={{ color: '#bfbfbf' }}>(em breve)</span></div>
                            <InputNumber
                                min={1} max={1000}
                                value={editForm.limits.maxComments}
                                onChange={function(v) { updateLimits('maxComments', v); }}
                                disabled={saving}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div>
                            <div style={{ fontSize: 11, color: '#595959', marginBottom: 2 }}>Posts analisados <span style={{ color: '#bfbfbf' }}>(em breve)</span></div>
                            <InputNumber
                                min={1} max={20}
                                value={editForm.limits.maxPostsPerProfile}
                                onChange={function(v) { updateLimits('maxPostsPerProfile', v); }}
                                disabled={saving}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Passo 5 — Auto-promote */}
                <StepHeader
                    number={5}
                    title="Transformar contatos em leads?"
                />
                <div style={{
                    marginBottom: 14, padding: 10,
                    background: editForm.auto_promote ? '#f6ffed' : '#fff',
                    border: '1px solid ' + (editForm.auto_promote ? '#b7eb8f' : '#f0f0f0'),
                    borderRadius: 6,
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                }}>
                    <Switch
                        checked={!!editForm.auto_promote}
                        onChange={function(v) { updateForm({ auto_promote: v }); }}
                        disabled={saving}
                        style={{ marginTop: 2 }}
                    />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#262626' }}>
                            Criar lead automaticamente
                            {editForm.has_laravel_service_key && (
                                <Icon type="link" style={{ marginLeft: 6, color: '#52c41a', fontSize: 11 }} title="Conexão configurada" />
                            )}
                        </div>
                        <div style={{ fontSize: 11, color: '#595959', marginTop: 2, lineHeight: 1.5 }}>
                            {editForm.auto_promote ? (
                                <span>
                                    Cada contato novo vira lead em {editForm.destination ? <strong>{DESTINATION_META[editForm.destination].short}</strong> : 'no destino escolhido'}
                                    {editForm.cadence_name && <span> com a cadência <strong>{editForm.cadence_name}</strong></span>}.
                                    Já existentes (mesmo @) são ignorados.
                                    {!editForm.cadence_name && editForm.destination && (
                                        <span style={{ display: 'block', marginTop: 4, color: '#ad4e00' }}>
                                            <Icon type="bulb" style={{ marginRight: 4 }} />
                                            Dica: escolha uma cadência no passo 4 para o lead já entrar num fluxo de abordagem.
                                        </span>
                                    )}
                                </span>
                            ) : (
                                <span>
                                    Quando ligado, cada contato encontrado vira automaticamente um lead {editForm.destination ? 'em ' + DESTINATION_META[editForm.destination].short : ''}.
                                    Desligado, os contatos ficam guardados aqui para você consultar no histórico de buscas.
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <Button onClick={cancelEdit} disabled={saving}>Cancelar</Button>
                    <Button type="primary" icon="save" loading={saving} onClick={saveIntegration}>
                        {editForm.id == null ? 'Criar integração' : 'Salvar'}
                    </Button>
                </div>
            </div>
        );
    }

    function renderIntegrationCard(integration) {
        var isExpanded = expandedId === integration.id;
        var destMeta = DESTINATION_META[integration.destination];
        var lastRun = (integration.recent_runs && integration.recent_runs[0]) || null;
        var isScraping = scrapingId === integration.id;
        // Por que o "Buscar agora" está desabilitado — mostrado como tooltip
        // para o usuário saber o que falta configurar.
        var scrapeBlockedReason = !integration.is_active
            ? 'Integração inativa.'
            : !integration.has_token
                ? 'Configure a chave do Apify para poder buscar.'
                : integration.target_usernames.length === 0
                    ? 'Adicione ao menos um perfil do Instagram para poder buscar.'
                    : null;

        return (
            <div
                key={integration.id}
                style={{
                    background: '#fff',
                    border: '1px solid ' + (isExpanded ? '#FF7A00' : '#f0f0f0'),
                    borderRadius: 8,
                    marginBottom: 12,
                    overflow: 'hidden',
                    transition: 'border-color 0.15s',
                    boxShadow: isExpanded ? '0 2px 8px rgba(255, 122, 0, 0.08)' : 'none',
                }}
            >
                {/* Card header */}
                <div
                    onClick={function() { isExpanded ? cancelEdit() : startEdit(integration); }}
                    style={{
                        padding: 14,
                        display: 'flex', alignItems: 'center', gap: 12,
                        cursor: 'pointer',
                    }}
                >
                    <div style={{
                        width: 36, height: 36, borderRadius: 8,
                        background: destMeta ? destMeta.bg : '#fafafa',
                        border: '1px solid ' + (destMeta ? destMeta.border : '#f0f0f0'),
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <Icon type="instagram" style={{ fontSize: 18, color: destMeta ? destMeta.color : '#8c8c8c' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>
                                {integration.name || '(sem nome)'}
                            </div>
                            {renderDestinationChip(integration.destination)}
                            {integration.cadence_name && (
                                <span style={{ fontSize: 10, color: '#8c8c8c' }}>
                                    <Icon type="schedule" style={{ marginRight: 3 }} />
                                    {integration.cadence_name}
                                </span>
                            )}
                            {!integration.is_active && <Tag style={{ fontSize: 10, marginLeft: 'auto' }}>Inativa</Tag>}
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 3 }}>
                            {integration.target_usernames.length} {integration.target_usernames.length === 1 ? 'perfil' : 'perfis'}
                            {' · '}
                            <strong style={{ color: '#262626' }}>{integration.contacts_count || 0}</strong> contato(s)
                            {lastRun && (
                                <span> · última busca {moment(lastRun.started_at).fromNow()}</span>
                            )}
                        </div>
                        {scrapeBlockedReason && integration.is_active && (
                            <div style={{ fontSize: 10, color: '#ad4e00', marginTop: 2 }}>
                                <Icon type="exclamation-circle" style={{ marginRight: 3 }} />
                                {scrapeBlockedReason}
                            </div>
                        )}
                    </div>
                    <span
                        title={scrapeBlockedReason || 'Buscar contatos destes perfis agora'}
                        onClick={function(e) { e.stopPropagation(); }}
                    >
                        <Button
                            type="primary"
                            ghost
                            size="small"
                            icon={isScraping ? 'loading' : 'thunderbolt'}
                            loading={isScraping}
                            disabled={isScraping || !!scrapeBlockedReason}
                            onClick={function(e) { e.stopPropagation(); runScrape(integration); }}
                            style={scrapeBlockedReason ? undefined : { borderColor: '#FF7A00', color: isScraping ? undefined : '#FF7A00' }}
                        >
                            {isScraping ? 'Buscando…' : 'Buscar agora'}
                        </Button>
                    </span>
                    <Icon
                        type={isExpanded ? 'up' : 'down'}
                        style={{ fontSize: 12, color: '#bfbfbf', flexShrink: 0 }}
                    />
                </div>

                {/* Card body — runs log + edit form */}
                {isExpanded && (
                    <>
                        <div style={{ padding: '0 16px 14px', borderTop: '1px solid #f0f0f0' }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#595959', marginTop: 12, marginBottom: 2 }}>
                                <Icon type="history" style={{ marginRight: 4 }} />
                                Histórico de buscas
                            </div>
                            <div style={{ fontSize: 10, color: '#bfbfbf', marginBottom: 4 }}>
                                Além das buscas manuais, o robô roda sozinho todos os dias às 07h e às 13h.
                            </div>
                            {renderRunsLog(integration.recent_runs, integration.contacts_count)}
                        </div>
                        {renderEditForm()}
                        <div style={{ padding: '10px 16px', background: '#fafafa', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
                            <Popconfirm
                                title="Remover esta integração? Os contatos já capturados serão preservados."
                                okText="Remover"
                                cancelText="Cancelar"
                                okType="danger"
                                onConfirm={function() { deleteIntegration(integration.id); }}
                            >
                                <Button type="link" size="small" style={{ color: '#bfbfbf', fontSize: 11 }}>
                                    <Icon type="delete" /> Remover integração
                                </Button>
                            </Popconfirm>
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <>
        <Drawer
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: 11,
                        background: 'linear-gradient(135deg, #ff7a00 0%, #ff4e00 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 3px 10px rgba(255, 122, 0, 0.25)', flexShrink: 0,
                    }}>
                        <Icon type="thunderbolt" theme="filled" style={{ fontSize: 20, color: '#fff' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#262626', lineHeight: 1.2 }}>
                            Buscar contatos pelo Instagram
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            Crie integrações independentes pra cada perfil estratégico
                        </div>
                    </div>
                </div>
            }
            placement="right"
            width={580}
            visible={visible}
            onClose={onClose}
            zIndex={1500}
            bodyStyle={{ padding: '16px 20px', background: '#fafafa' }}
        >
            {/* Banner */}
            <div style={{
                background: 'linear-gradient(135deg, #fff7e6 0%, #fff1d4 100%)',
                border: '1px solid #ffd591',
                borderRadius: 8, padding: 14, marginBottom: 14,
            }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#ad4e00', marginBottom: 8 }}>
                    <Icon type="rocket" theme="filled" style={{ marginRight: 6 }} />
                    Como funciona
                </div>
                {[
                    <span><strong>Conecte sua conta Apify</strong> — o robô que faz as buscas no Instagram. Não tem conta? O passo a passo aparece na hora de criar a integração.</span>,
                    <span><strong>Escolha os perfis estratégicos</strong> — concorrentes, parceiros ou o seu próprio perfil.</span>,
                    <span><strong>O robô busca os seguidores sozinho</strong>, todos os dias às 07h e às 13h — ou na hora, com "Buscar agora".</span>,
                    <span><strong>Os contatos podem virar leads automaticamente</strong> em Social Selling ou Prospecção Ativa, já com cadência aplicada.</span>,
                ].map(function(text, i) {
                    return (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: i === 3 ? 0 : 7 }}>
                            <div style={{
                                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                                border: '1px solid #ffd591', color: '#ad4e00',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 2,
                            }}>
                                {i + 1}
                            </div>
                            <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.55, flex: 1 }}>{text}</div>
                        </div>
                    );
                })}
            </div>

            {/* Add new — only show if no form is open */}
            {expandedId !== '__new__' && (
                <Button
                    type="dashed"
                    icon="plus"
                    onClick={startNew}
                    style={{ width: '100%', marginBottom: 14, height: 44, fontWeight: 500, color: '#FF7A00', borderColor: '#FF7A00' }}
                >
                    Adicionar integração
                </Button>
            )}

            {/* List */}
            {loadingList ? (
                <IntegrationsListSkeleton />
            ) : integrations.length === 0 && expandedId !== '__new__' ? (
                <div style={{
                    background: '#fff', border: '1px dashed #d9d9d9', borderRadius: 8,
                    padding: 28, textAlign: 'center',
                }}>
                    <Icon type="inbox" style={{ fontSize: 32, color: '#bfbfbf', marginBottom: 8 }} />
                    <div style={{ fontSize: 13, color: '#595959', marginBottom: 4 }}>Nenhuma integração ainda</div>
                    <div style={{ fontSize: 11, color: '#8c8c8c', lineHeight: 1.6, maxWidth: 340, margin: '0 auto' }}>
                        Clique em "Adicionar integração" para criar a primeira.
                        Você só vai precisar de uma conta gratuita no Apify — o passo a passo aparece durante a criação.
                    </div>
                </div>
            ) : (
                integrations.map(renderIntegrationCard)
            )}
        </Drawer>

        {/* Nova integração — opens as a drawer over the main one */}
        <Drawer
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 9,
                        background: 'linear-gradient(135deg, #ff7a00 0%, #ff4e00 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        boxShadow: '0 3px 10px rgba(255, 122, 0, 0.25)',
                    }}>
                        <Icon type="plus" style={{ fontSize: 16, color: '#fff' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', lineHeight: 1.2 }}>
                            Nova integração
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            Um workflow independente para cada perfil estratégico
                        </div>
                    </div>
                </div>
            }
            placement="right"
            width={520}
            visible={expandedId === '__new__' && !!editForm}
            onClose={cancelEdit}
            zIndex={1550}
            destroyOnClose
            bodyStyle={{ padding: 0, background: '#fafafa' }}
        >
            {expandedId === '__new__' && editForm ? renderEditForm() : null}
        </Drawer>

        {/* Contacts modal — drilldown into a specific run */}
        <Modal
            visible={!!contactsModalRun}
            onCancel={closeContactsModal}
            footer={null}
            width={720}
            zIndex={1600}
            title={contactsModalRun ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: 'linear-gradient(135deg, #f56040 0%, #c13584 50%, #833ab4 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <Icon type="instagram" style={{ fontSize: 18, color: '#fff' }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', lineHeight: 1.2 }}>
                            Contatos de <span style={{ fontFamily: 'monospace' }}>@{contactsModalRun.target_username}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 400, marginTop: 2 }}>
                            {modalContactsLoading ? 'Carregando…' : modalContactsTotal > 0 ? (
                                <span><strong style={{ color: '#262626' }}>{modalContactsTotal}</strong> {modalContactsTotal === 1 ? 'contato encontrado' : 'contatos encontrados'} no total</span>
                            ) : 'Nenhum contato'}
                        </div>
                    </div>
                </div>
            ) : null}
        >
            {modalContactsLoading ? (
                <ContactsTableSkeleton rows={6} />
            ) : (
                <Table
                    rowKey="id"
                    columns={contactsColumns}
                    dataSource={modalContacts}
                    size="small"
                    pagination={modalContacts.length > 25 ? { pageSize: 25, showSizeChanger: false } : false}
                    locale={{ emptyText: 'Nenhum contato encontrado para este perfil.' }}
                />
            )}
        </Modal>
        </>
    );
};

export default ApifyIntegrationDrawer;
